import {objectFromEntries} from '@benchling/js-utils';

import {MeasurementUnit} from 'constants/__generated/MeasurementUnit';
import {isDensityUnit, isMassUnit, isMolarityUnit, isVolumeUnit} from 'inventory/InventoryUnitUtil';
import {StoichiometryCalculationMode} from 'notebook/integrations/chemistry/ChemistryStructuredTableConstants';
import {
  preciseDecimalToTypedValue,
  typedValueToPreciseDecimal,
} from 'notebook/integrations/chemistry/custom-evals/ReactionCustomEvalUtils';
import {StoichiometryVariable} from 'notebook/integrations/chemistry/custom-evals/stoichiometry-calculations/StoichiometryEquations';
import {applyStoichiometrySigFigs} from 'notebook/integrations/chemistry/custom-evals/stoichiometry-calculations/StoichiometrySignificantFigures';
import StoichiometrySolver, {
  StoichiometryCalculationVariableValues,
} from 'notebook/integrations/chemistry/custom-evals/stoichiometry-calculations/StoichiometrySolver';
import {
  getStandardChemistryUnitConversionFactor,
  KG_TO_G_CONVERSION_FACTOR,
  resolveUnitsFromTypedValues,
} from 'notebook/integrations/chemistry/custom-evals/stoichiometry-calculations/StoichiometryUnits';
import {StoichiometryCustomEvalOptions} from 'notebook/integrations/chemistry/StoichiometryStructuredTableAdapter';
import {ColumnDefinition} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {CustomEval, CustomEvalResult} from 'notebook/tables/TableConstants';
import {
  createFloatTypedValue,
  isTypedValueEmpty,
  isUnitReferenceTypedValue,
} from 'notebook/tables/typed-values/DataTypes';
import {TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {ResolveQueryFn} from 'notebook/tables/typed-values/TypedValueResolution';
import PreciseDecimal from 'util/precise-decimal/PreciseDecimal';
import {Optional} from 'util/Types';

export type MolesActualCellDependencies = [
  amountActual: TypedValue | null,
  amountUnit: TypedValue | null,
  molecularWeight: TypedValue | null,
  molUnit: TypedValue | null,
  moleculePurity: TypedValue | null,
  concentration: TypedValue | null,
  concentrationUnit: TypedValue | null,
  formulaWeight: TypedValue | null,
];

function cellDependenciesToVariableValues(
  cellDependencies: MolesActualCellDependencies,
  units: Array<MeasurementUnit>
): StoichiometryCalculationVariableValues {
  const [
    amountActual,
    amountUnitTypedValue,
    molecularWeight,
    molUnitTypedValue,
    moleculePurity,
    concentration,
    concentrationUnitTypedValue,
    formulaWeight,
  ] = cellDependencies;

  const unitsById = objectFromEntries(units.map((unit) => [unit.id, unit]));
  const amountUnit = isUnitReferenceTypedValue(amountUnitTypedValue)
    ? unitsById[amountUnitTypedValue.unitSummary.id]
    : undefined;
  const molUnit = isUnitReferenceTypedValue(molUnitTypedValue)
    ? unitsById[molUnitTypedValue.unitSummary.id]
    : undefined;
  const concentrationUnit = isUnitReferenceTypedValue(concentrationUnitTypedValue)
    ? unitsById[concentrationUnitTypedValue.unitSummary.id]
    : undefined;

  const molConversion = getStandardChemistryUnitConversionFactor(molUnit);
  const moleculePurityPercentage = typedValueToPreciseDecimal(moleculePurity);

  // TODO(BNCH-154354): Remove MW fallback once all tables have FW column
  const effectiveFormulaWeight =
    typedValueToPreciseDecimal(formulaWeight) ?? typedValueToPreciseDecimal(molecularWeight);

  const values: StoichiometryCalculationVariableValues = {
    [StoichiometryVariable.FORMULA_WEIGHT]: effectiveFormulaWeight,
    [StoichiometryVariable.MOL_UNIT_CONVERSION]: molConversion,
    [StoichiometryVariable.MOLECULE_PURITY]:
      moleculePurityPercentage &&
      moleculePurityPercentage.decimal.gt(0) &&
      moleculePurityPercentage.decimal.lte(100)
        ? PreciseDecimal.of(moleculePurityPercentage.decimal.div(100))
        : undefined,
  };

  if (amountUnit) {
    if (isMassUnit(amountUnit)) {
      values[StoichiometryVariable.MASS] = typedValueToPreciseDecimal(amountActual);
      values[StoichiometryVariable.MASS_UNIT_CONVERSION] = getStandardChemistryUnitConversionFactor(
        amountUnit,
        KG_TO_G_CONVERSION_FACTOR
      );
    } else if (isVolumeUnit(amountUnit)) {
      values[StoichiometryVariable.VOLUME] = typedValueToPreciseDecimal(amountActual);
      values[StoichiometryVariable.VOLUME_UNIT_CONVERSION] =
        getStandardChemistryUnitConversionFactor(amountUnit);
    }
  }

  if (concentrationUnit) {
    if (isMolarityUnit(concentrationUnit)) {
      values[StoichiometryVariable.MOLARITY] = typedValueToPreciseDecimal(concentration);
      values[StoichiometryVariable.MOLARITY_UNIT_CONVERSION] =
        getStandardChemistryUnitConversionFactor(concentrationUnit);
    } else if (isDensityUnit(concentrationUnit)) {
      values[StoichiometryVariable.DENSITY] = typedValueToPreciseDecimal(concentration);
      values[StoichiometryVariable.DENSITY_UNIT_CONVERSION] = getStandardChemistryUnitConversionFactor(
        concentrationUnit,
        KG_TO_G_CONVERSION_FACTOR
      );
    }
  }

  return values;
}

const molesActualCustomEval: CustomEval<StoichiometryCustomEvalOptions> = (
  typedValue: TypedValue,
  columnDefinition: Optional<ColumnDefinition>,
  cellDependencies: Array<TypedValue | null>,
  customEvalOptions: StoichiometryCustomEvalOptions,
  resolveFromResolutionQuery: ResolveQueryFn
): CustomEvalResult => {
  const {
    tableMetadata: {calculationMode = StoichiometryCalculationMode.STANDARD},
  } = customEvalOptions;
  const amountActual = cellDependencies[0];
  // If there is no amount actual value, return empty and don't lock.
  if (!amountActual || isTypedValueEmpty(amountActual)) {
    return {val: createFloatTypedValue(null), error: null, locked: false};
  }

  // Load any units used in the calculation.
  const {units, error: unitsResolutionError} = resolveUnitsFromTypedValues(
    cellDependencies,
    resolveFromResolutionQuery
  );
  if (unitsResolutionError != null) {
    return {val: typedValue, error: unitsResolutionError, locked: false};
  }

  const variableValues = cellDependenciesToVariableValues(
    cellDependencies as MolesActualCellDependencies,
    units ?? []
  );

  const solverResult = StoichiometrySolver.solve(StoichiometryVariable.MOLS, variableValues);

  // eslint-disable-next-line benchlint/no-in-operator
  if ('missingDeps' in solverResult) {
    return {
      val: createFloatTypedValue(null),
      error: {type: 'ERROR', message: `Missing dependencies: ${[...solverResult.missingDeps].join(', ')}`},
      locked: false,
    };
  }

  const {solution, equation} = solverResult;
  let roundedSolution = solution;
  if (calculationMode === StoichiometryCalculationMode.SIGFIGS) {
    roundedSolution = applyStoichiometrySigFigs(solution, variableValues, equation);
  }
  return {
    val: preciseDecimalToTypedValue(roundedSolution),
    error: null,
    locked: true,
  };
};

export default molesActualCustomEval;
